import {Component, inject, OnDestroy, OnInit} from "@angular/core";
import {RouterOutlet} from "@angular/router";
import {NgIf} from '@angular/common';
import {LayoutService} from './layout.service';
import {delay, Subject, takeUntil, tap} from 'rxjs';
import {Broker, ColorScheme} from '../../core/types/page-configuration.types';
import {ErrorPageComponent} from "../shortlink-container/pages/error-page/error-page.component";
import {ErrorService} from "../../core/services/error.service";
import {ErrorModalComponent} from "../shared/error-modal/error-modal.component";

@Component({
    selector: "app-layout",
    standalone: true,
    imports: [RouterOutlet, NgIf, ErrorPageComponent, ErrorModalComponent],
    templateUrl: "./layout.component.html"
})
export class LayoutComponent implements OnInit, OnDestroy {
    private _layoutService = inject(LayoutService);
    private _errorService = inject(ErrorService);
    private readonly _unsubscribeAll: Subject<any> = new Subject<any>();

    logoSrc: string | null = null;
    brokerInfo: Broker | null = null;
    errorMessage: string | null = null;

    ngOnInit(): void {
        this.initializeStyling();
    }

    initializeStyling(): void {
        this._layoutService.colorScheme$
            .pipe(
                // delay is added to prevent expression changed after it was checked
                delay(0),
                tap((colorScheme: ColorScheme | null) => {
                    this.setStyling(colorScheme);
                }),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe();

        this._layoutService.logoSrc$
            .pipe(
                // delay is added to prevent expression changed after it was checked
                delay(0),
                tap((logoSrc: string | null) => {
                    this.logoSrc = logoSrc;
                }),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe();

        this._layoutService.brokerInfo$
            .pipe(
                // delay is added to prevent expression changed after it was checked
                delay(0),
                tap((brokerInfo: Broker | null) => {
                    this.brokerInfo = brokerInfo;
                }),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe();

        this._errorService.errorMessage$
            .pipe(
                // delay is added to prevent expression changed after it was checked
                delay(0),
                tap((errorMessage: string | null) => {
                    this.errorMessage = errorMessage;
                }),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    private setStyling(colorScheme: ColorScheme | null): void {
        if (!colorScheme) return;

        const root = document.documentElement;
        root.style.setProperty('--main-color', colorScheme.mainColor);
        root.style.setProperty('--main-color-light', `hsl(${colorScheme.mainColorLight})`);
        root.style.setProperty('--secondary-color', colorScheme.secondaryColor);
        root.style.setProperty('--form-header', colorScheme.formHeader);
        root.style.setProperty('--form-accent', `hsla(${colorScheme.formAccent})`);
        root.style.setProperty('--border-color', `hsla(${colorScheme.borderColor})`);
        root.style.setProperty('--label-dark', `hsla(${colorScheme.labelDark})`);
        root.style.setProperty('--input-color', `hsla(${colorScheme.inputColor})`);
    }
    
    get brokerInfoExists(): boolean {
        return this.brokerNameExists
            || this.brokerEmailExists
            || this.brokerPhoneExists; 
    }
    
    get brokerNameExists(): boolean {
        return !!this.brokerInfo?.name;
    }
    
    get brokerEmailExists(): boolean {
        return !!this.brokerInfo?.email;
    }
    
    get brokerPhoneExists(): boolean {
        return !!this.brokerInfo?.phone;
    }
    
    get emailHref(): string {
        return 'mailto:' + this.brokerInfo?.email ?? '';
    }
    
    get phoneHref(): string {
        return 'tel:' + this.brokerInfo?.phone ?? ''
    }

    onLogoError(event: Event): void {
        const container = (event.target as HTMLElement).closest('.header-logo') as HTMLElement;
        if (container) container.style.display = 'none';
    }
}
